/**
 * Imports the necessary dependencies for a React component that includes a custom button and a modal from react-bootstrap.
 * @module React
 * @module CustomButton
 * @module Modal
 */
import React from "react";
import CustomButton from "../customButton/customButton";
import { Modal } from "react-bootstrap";

/**
 * A reusable component that renders a confirmation modal with customizable options.
 * @param {object} props - The props object containing the following properties:
 *   - iconColor: The color of the icon in the modal.
 *   - modalTitle: The title of the modal.
 *   - isFirstButton: A boolean indicating whether the first button should be displayed.
 *   - isSecondButton: A boolean indicating whether the second button should be displayed.
 *   - isUnderline: A boolean indicating whether the message link should be underlined.
 *   - isBold: A boolean indicating whether the message should be bold.
 *   - isModelIcon: A boolean indicating whether an icon should be displayed in the modal.
 *   -
 */
const ConfirmModal = ({
  className = "confirmModal",
  modalContent = true,
  ...props
}) => {
  /**
   * Destructures the properties object and assigns the corresponding values to variables.
   * @param {object} props - The properties object containing the values to destructure.
   * @returns None
   */
  const {
    iconColor,
    modalTitle,
    isFirstButton,
    isSecondButton,
    isUnderline,
    isBold,
    isModelIcon,
    closeModelIcon,
    leftdisabled,
    rightdisabled,
    rightButtonClick,
    onProcced,
    isCloseBtn,
    onHide,
    loaderButtonShow,
    showModal,
    iconCricle,
    messageLink,
    modalDescription,
    messagebold,
    children,
    leftBtnTitle,
    leftBtnIcon,
    leftBtnClass,
    rightBtnTitle,
    rightBtnIcon,
    rightBtnClass
  } = props;

  return (
    /**
     * Renders a modal component with customizable content and buttons.
     * @param {string} className - Additional class names to apply to the modal.
     * @param {boolean} showModal - Determines whether the modal is shown or hidden.
     * @param {function} onHide - Callback function to handle hiding the modal.
     * @param {boolean} centered - Determines whether the modal is centered on the screen.
     * @param {boolean} isModelIcon - Determines whether to display an icon in the modal.
     * @param {string} iconCricle - Additional class names to apply to the icon circle.
     * @param {string} iconColor - Additional class names to apply to the icon.
     * @param {string} modalTitle - The title of
     */
    <Modal
      className={"confirmModal " + className}
      show={showModal}
      onHide={onHide}
      centered
    >
      <Modal.Body>
        {isModelIcon && (
          <div className={"iconCricleStyle " + iconCricle}>
            <i className={"icon " + iconColor} />
          </div>
        )}
        <div className="Title-IconWrapper">
          <div className="modalTitle">
            <h4>{modalTitle}</h4>
          </div>
          {isCloseBtn && (
            <div className="CloseIcon" onClick={closeModelIcon}>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
              ></button>
            </div>
          )}
        </div>

        {modalContent && (
          <p className="modalContent">
            {modalDescription} {isUnderline && <u>{messageLink}</u>}{" "}
            {isBold && <b>{messagebold}</b>}
          </p>
        )}
        {children}
      </Modal.Body>
      <div className="modal-footer">
        {isFirstButton && (
          <CustomButton
            type="submit"
            title={leftBtnTitle}
            isLeftIcon={true}
            disabled={leftdisabled}
            icon={leftBtnIcon}
            className={leftBtnClass}
            onClick={onProcced}
            loading={loaderButtonShow}
          />
        )}
        {isSecondButton && (
          <CustomButton
            type="button"
            title={rightBtnTitle}
            isLeftIcon={true}
            icon={rightBtnIcon}
            disabled={rightdisabled}
            className={rightBtnClass}
            onClick={rightButtonClick}
          />
        )}
      </div>
    </Modal>
  );
};

export default ConfirmModal;
